import React, { useContext, useState, useEffect, useCallback } from 'react';
import { AppContext } from '../App';
import Layout from '../components/Layout';
import { authAxios } from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { GripVertical, Check, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';

export default function PlayerDraftRankings() {
  const { user, currentSeason, API } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contestants, setContestants] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [draftStarted, setDraftStarted] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [notParticipant, setNotParticipant] = useState(false);

  const loadData = useCallback(async () => {
    if (!currentSeason) return;
    
    try {
      setLoading(true);
      const response = await authAxios.get(`${API}/seasons/${currentSeason.id}/draft/rankings`);
      
      setContestants(response.data.contestants || []);
      setSubmittedAt(response.data.submitted_at);
      
      // If user has rankings, use them; otherwise show all contestants unranked
      if (response.data.rankings && response.data.rankings.length > 0) {
        setRankings(response.data.rankings);
      } else {
        // Initialize with all contestants in default order
        setRankings(response.data.contestants?.map(c => c.id) || []);
      }
      
      // Check if draft has started
      const statusResponse = await authAxios.get(`${API}/seasons/${currentSeason.id}/draft/rankings/status`);
      setDraftStarted(statusResponse.data.draft_started);
      
      // Check if user is a participant
      if (!statusResponse.data.is_participant) {
        setNotParticipant(true);
      }
      
    } catch (error) {
      console.error('Failed to load rankings data:', error);
      if (error.response?.status === 403) {
        setNotParticipant(true);
        toast.error('You are not a participant in this draft');
      } else if (error.response?.status === 400 && error.response?.data?.detail?.includes('not in auto-draft mode')) {
        toast.error('This draft is not in auto-draft mode');
      } else {
        toast.error('Failed to load rankings');
      }
    } finally {
      setLoading(false);
    }
  }, [API, currentSeason]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const saveRankings = async (newRankings) => {
    try {
      setSaving(true);
      await authAxios.post(`${API}/seasons/${currentSeason.id}/draft/rankings`, {
        rankings: newRankings
      });
      toast.success('Rankings saved');
    } catch (error) {
      console.error('Failed to save rankings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save rankings');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await authAxios.post(`${API}/seasons/${currentSeason.id}/draft/rankings/submit`);
      setSubmittedAt(new Date().toISOString());
      toast.success('Rankings submitted! Your preferences are locked in.');
    } catch (error) {
      console.error('Failed to submit rankings:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit rankings');
    } finally {
      setSubmitting(false);
    }
  };

  const moveContestant = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= rankings.length) return;
    
    const newRankings = [...rankings];
    const [moved] = newRankings.splice(fromIndex, 1);
    newRankings.splice(toIndex, 0, moved);
    setRankings(newRankings);
    saveRankings(newRankings);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newRankings = [...rankings];
    const [moved] = newRankings.splice(draggedIndex, 1);
    newRankings.splice(index, 0, moved);
    setRankings(newRankings);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null) {
      saveRankings(rankings);
    }
    setDraggedIndex(null);
  };

  const resetToDefault = () => {
    const defaultOrder = contestants.map(c => c.id);
    setRankings(defaultOrder);
    saveRankings(defaultOrder);
    toast.info('Rankings reset to default order');
  };

  const getContestantById = (id) => contestants.find(c => c.id === id);

  if (!currentSeason) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-600">No season selected</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading rankings...</p>
        </div>
      </Layout>
    );
  }

  if (draftStarted) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto container-mobile">
          <Card>
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-2">Draft Complete!</h2>
              <p className="text-slate-600">
                The auto-draft has been run. Check out the draft replay to see who got which contestants!
              </p>
              <Button 
                className="mt-4"
                onClick={() => window.location.href = `/draft/${currentSeason.id}/replay`}
              >
                View Draft Replay
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (notParticipant) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto container-mobile">
          <Card>
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🚫</span>
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-2">Not in This Draft</h2>
              <p className="text-slate-600">
                You're not a participant in this draft. This can happen if you joined the league after the draft was configured.
              </p>
              <p className="text-slate-500 text-sm mt-4">
                Contact your League Admin to be added, or wait for the next season's draft.
              </p>
              <Button 
                variant="outline"
                className="mt-4"
                onClick={() => window.location.href = '/'}
              >
                Back to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto container-mobile">
        <div className="spacing-section">
          <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Rank Your Contestants
          </h1>
          <p className="text-responsive text-slate-600">
            Drag to reorder or use the arrows. Your top choices will be picked first!
          </p>
        </div>

        {/* Status Card */}
        <Card className="mb-6">
          <CardContent className="p-4">
            {submittedAt ? (
              <div className="flex items-center gap-3 text-green-800 bg-green-50 p-3 rounded-lg">
                <Check className="w-5 h-5" />
                <div>
                  <p className="font-semibold">Rankings Submitted!</p>
                  <p className="text-sm">Your preferences are locked in. The admin will run the draft soon.</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 text-amber-800 bg-amber-50 p-3 rounded-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-semibold">Rank your contestants before the draft!</p>
                  <p className="text-sm">You can edit until you submit or the admin runs the draft.</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* How it works */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-2">How Auto-Draft Works</h3>
            <ul className="text-sm text-slate-600 space-y-1 list-disc ml-4">
              <li>Rank all contestants from your most wanted (#1) to least wanted</li>
              <li>When it's your turn in the draft, the system picks your highest-ranked <em>available</em> contestant</li>
              <li>If your top choices are taken, the system moves down your list automatically</li>
              <li>Click "Submit Rankings" when you're happy with your order</li>
            </ul>
          </CardContent>
        </Card>

        {/* Rankings List */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Your Rankings</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={resetToDefault}
                disabled={submittedAt}
                className="text-slate-500"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Reset
              </Button>
            </div>
            
            <div className="space-y-2">
              {rankings.map((contestantId, index) => {
                const contestant = getContestantById(contestantId);
                if (!contestant) return null;
                
                return (
                  <div
                    key={contestantId}
                    draggable={!submittedAt}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-3 p-3 bg-white border rounded-lg transition-all ${
                      draggedIndex === index ? 'opacity-50 border-amber-400' : 'border-slate-200'
                    } ${submittedAt ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'}`}
                  >
                    {/* Rank Number */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      index < 3 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {index + 1}
                    </div>
                    
                    {/* Drag Handle */}
                    {!submittedAt && (
                      <GripVertical className="w-5 h-5 text-slate-400" />
                    )}
                    
                    {/* Contestant Photo */}
                    {contestant.photo_url ? (
                      <img 
                        src={contestant.photo_url} 
                        alt={contestant.full_name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                        <span className="text-slate-500 text-sm font-medium">
                          {contestant.full_name?.charAt(0)}
                        </span>
                      </div>
                    )}
                    
                    {/* Contestant Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{contestant.full_name}</p>
                      {contestant.age && (
                        <p className="text-xs text-slate-500">{contestant.age}, {contestant.hometown}</p>
                      )}
                    </div>
                    
                    {/* Move Buttons */}
                    {!submittedAt && (
                      <div className="flex flex-col gap-0.5">
                        <button
                          onClick={() => moveContestant(index, index - 1)}
                          disabled={index === 0}
                          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowUp className="w-4 h-4 text-slate-500" />
                        </button>
                        <button
                          onClick={() => moveContestant(index, index + 1)}
                          disabled={index === rankings.length - 1}
                          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowDown className="w-4 h-4 text-slate-500" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {saving && (
              <p className="text-xs text-slate-500 mt-2 text-center">Saving...</p>
            )}
          </CardContent>
        </Card>

        {/* Submit Button */}
        {!submittedAt && (
          <Button 
            size="lg" 
            className="w-full bg-purple-600 hover:bg-purple-700"
            onClick={handleSubmit}
            disabled={submitting || rankings.length === 0}
          >
            {submitting ? 'Submitting...' : 'Submit Rankings'}
          </Button>
        )}
      </div>
    </Layout>
  );
}
